import type { ComponentType } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { resolveStatusConfig, TERMINAL_STATUSES } from '@/lib/executionStatus';
import { formatOutputCounts } from '@/utils/formatOutputCounts';
import type { ConnectorExecution } from '@/types/connectors';

interface Props {
  latestExecution: ConnectorExecution | null | undefined;
}

type Tone = 'success' | 'error' | 'warning' | 'neutral';

function relativeTime(iso: string | null | undefined): string {
  if (!iso) return 'just now';
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

const TONE_BORDER: Record<Tone, string> = {
  success: 'border-border',
  error: 'border-destructive/30 bg-destructive/5',
  warning: 'border-warning/30 bg-warning/5',
  neutral: 'border-border',
};
const TONE_CIRCLE: Record<Tone, string> = {
  success: 'bg-success/15 text-success',
  error: 'bg-destructive/15 text-destructive',
  warning: 'bg-warning/15 text-warning',
  neutral: 'bg-muted text-muted-foreground',
};

function Banner({ tone, icon: Icon, iconClass, lead, sub }: {
  tone: Tone;
  icon: ComponentType<{ className?: string; 'aria-hidden'?: boolean }>;
  iconClass?: string;
  lead: string;
  sub?: string;
}) {
  return (
    <div role="status" aria-live="polite"
         className={cn('flex items-center gap-3 rounded-lg border bg-card px-4 py-3 text-sm', TONE_BORDER[tone])}>
      <span className={cn('flex h-8 w-8 flex-none items-center justify-center rounded-full', TONE_CIRCLE[tone])}>
        <Icon className={cn('h-4 w-4', iconClass)} aria-hidden />
      </span>
      <div className="min-w-0">
        <span className="font-semibold">{lead}</span>
        {sub && <span className="mt-0.5 block text-[13px] text-muted-foreground">{sub}</span>}
      </div>
    </div>
  );
}

export function ConnectorStatusBanner({ latestExecution }: Props) {
  if (!latestExecution) return null;
  const status = latestExecution.status;
  const dur = latestExecution.duration_seconds ? `${Math.round(latestExecution.duration_seconds)}s` : '—';

  if (status === 'pending' || status === 'running') {
    const rows = (latestExecution.records_processed ?? 0).toLocaleString();
    return (
      <Banner tone="warning" icon={Loader2} iconClass="animate-spin"
              lead="Running now"
              sub={`started ${relativeTime(latestExecution.started_at)} · ${rows} records so far`} />
    );
  }

  if (status === 'failed') {
    const reason = (latestExecution as any).error_message ?? 'see Activity for details';
    return (
      <Banner tone="error" icon={XCircle}
              lead="Last run failed"
              sub={`${relativeTime(latestExecution.completed_at ?? latestExecution.started_at)} · ${reason}`} />
    );
  }

  if (status === 'success') {
    const rows = (latestExecution.records_processed ?? 0).toLocaleString();
    // #1009: files_created is structurally 0 for connectors that merge into
    // the lakehouse, so show tables written when no files were created.
    // #2235: when a run merged into tables, files_created counts MERGES (one per
    // output_paths entry), not physical files; the Files tile carries the real
    // count. Report tables here so the banner and the tiles cannot disagree.
    const tables = latestExecution.tables_updated ?? 0;
    const outputs = formatOutputCounts(tables > 0 ? 0 : (latestExecution.files_created ?? 0), tables);
    // Distinct datasets: a run that merges repeatedly into one table records one
    // output_paths entry per merge.
    const datasets = new Set(
      (latestExecution.output_paths ?? []).map((o: any) =>
        typeof o === 'string' ? o : (o?.dataset_id ?? o?.path)
      )
    ).size;
    return (
      <Banner tone="success" icon={CheckCircle}
              lead="Last run succeeded"
              sub={`${relativeTime(latestExecution.completed_at)} · ${dur} · ${rows} records · ${outputs} · ${datasets} datasets`} />
    );
  }

  // Terminal statuses that aren't success/failed (cancelled, partial, timeout, skipped).
  if (TERMINAL_STATUSES.has(status as any)) {
    const config = resolveStatusConfig(status);
    // #1009: a partial run carries the reason it degraded (which setting to
    // check) — surface it here the way the failed branch surfaces its reason,
    // rather than leaving a bare duration.
    const reason = (latestExecution as any).error_message;
    const when = relativeTime(latestExecution.completed_at ?? latestExecution.started_at);
    return (
      <Banner tone="neutral" icon={config.icon} iconClass={config.iconAnimation}
              lead={`Last run ${config.label.toLowerCase()}`}
              sub={reason ? `${when} · ${dur} · ${reason}` : `${when} · ${dur}`} />
    );
  }

  return null;
}
